'use client';

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import trTranslations from '@/locales/tr.json';
import enTranslations from '@/locales/en.json';
import frTranslations from '@/locales/fr.json';
import deTranslations from '@/locales/de.json';
import ruTranslations from '@/locales/ru.json';
import esTranslations from '@/locales/es.json';
import itTranslations from '@/locales/it.json';
import ptTranslations from '@/locales/pt.json';
import arTranslations from '@/locales/ar.json';
import zhTranslations from '@/locales/zh.json';

// ─── Supported Locale Codes (BCP 47) ─────────────────────────────────────────
export const LOCALE_CONFIGS = [
  { code: 'tr-TR', name: 'Türkçe', nativeName: 'Türkçe', flag: '🇹🇷', rtl: false, bcp47: 'tr-TR' },
  { code: 'en-US', name: 'English (US)', nativeName: 'English (United States)', flag: '🇺🇸', rtl: false, bcp47: 'en-US' },
  { code: 'en-GB', name: 'English (UK)', nativeName: 'English (United Kingdom)', flag: '🇬🇧', rtl: false, bcp47: 'en-GB' },
  { code: 'de-DE', name: 'Deutsch', nativeName: 'Deutsch', flag: '🇩🇪', rtl: false, bcp47: 'de-DE' },
  { code: 'fr-FR', name: 'Français', nativeName: 'Français', flag: '🇫🇷', rtl: false, bcp47: 'fr-FR' },
  { code: 'es-ES', name: 'Español', nativeName: 'Español', flag: '🇪🇸', rtl: false, bcp47: 'es-ES' },
  { code: 'it-IT', name: 'Italiano', nativeName: 'Italiano', flag: '🇮🇹', rtl: false, bcp47: 'it-IT' },
  { code: 'pt-PT', name: 'Português', nativeName: 'Português (Portugal)', flag: '🇵🇹', rtl: false, bcp47: 'pt-PT' },
  { code: 'pt-BR', name: 'Português (BR)', nativeName: 'Português (Brasil)', flag: '🇧🇷', rtl: false, bcp47: 'pt-BR' },
  { code: 'ru-RU', name: 'Русский', nativeName: 'Русский', flag: '🇷🇺', rtl: false, bcp47: 'ru-RU' },
  { code: 'ky-KG', name: 'Кыргызча', nativeName: 'Кыргызча', flag: '🇰🇬', rtl: false, bcp47: 'ky-KG' },
  { code: 'kk-KZ', name: 'Қазақша', nativeName: 'Қазақша', flag: '🇰🇿', rtl: false, bcp47: 'kk-KZ' },
  { code: 'uz-UZ', name: "O\'zbek", nativeName: "O\'zbek tili", flag: '🇺🇿', rtl: false, bcp47: 'uz-UZ' },
  { code: 'az-AZ', name: 'Azərbaycan', nativeName: 'Azərbaycan dili', flag: '🇦🇿', rtl: false, bcp47: 'az-AZ' },
  { code: 'ar-SA', name: 'العربية (SA)', nativeName: 'العربية (السعودية)', flag: '🇸🇦', rtl: true, bcp47: 'ar-SA' },
  { code: 'ar-AE', name: 'العربية (AE)', nativeName: 'العربية (الإمارات)', flag: '🇦🇪', rtl: true, bcp47: 'ar-AE' },
  { code: 'fa-IR', name: 'فارسی', nativeName: 'فارسی', flag: '🇮🇷', rtl: true, bcp47: 'fa-IR' },
  { code: 'zh-CN', name: '中文(简体)', nativeName: '中文（简体）', flag: '🇨🇳', rtl: false, bcp47: 'zh-CN' },
  { code: 'zh-TW', name: '中文(繁體)', nativeName: '中文（繁體）', flag: '🇹🇼', rtl: false, bcp47: 'zh-TW' },
  { code: 'ja-JP', name: '日本語', nativeName: '日本語', flag: '🇯🇵', rtl: false, bcp47: 'ja-JP' },
  { code: 'ko-KR', name: '한국어', nativeName: '한국어', flag: '🇰🇷', rtl: false, bcp47: 'ko-KR' },
  { code: 'hi-IN', name: 'हिन्दी', nativeName: 'हिन्दी', flag: '🇮🇳', rtl: false, bcp47: 'hi-IN' },
  { code: 'nl-NL', name: 'Nederlands', nativeName: 'Nederlands', flag: '🇳🇱', rtl: false, bcp47: 'nl-NL' },
  { code: 'pl-PL', name: 'Polski', nativeName: 'Polski', flag: '🇵🇱', rtl: false, bcp47: 'pl-PL' },
  { code: 'uk-UA', name: 'Українська', nativeName: 'Українська', flag: '🇺🇦', rtl: false, bcp47: 'uk-UA' },
  { code: 'ro-RO', name: 'Română', nativeName: 'Română', flag: '🇷🇴', rtl: false, bcp47: 'ro-RO' },
  { code: 'bg-BG', name: 'Български', nativeName: 'Български', flag: '🇧🇬', rtl: false, bcp47: 'bg-BG' },
  { code: 'el-GR', name: 'Ελληνικά', nativeName: 'Ελληνικά', flag: '🇬🇷', rtl: false, bcp47: 'el-GR' },
  { code: 'ka-GE', name: 'ქართული', nativeName: 'ქართული', flag: '🇬🇪', rtl: false, bcp47: 'ka-GE' },
] as const;

export type LocaleCode = typeof LOCALE_CONFIGS[number]['code'];

// Legacy 2-letter codes mapped to full BCP 47 codes for backward compat
export type AppLanguage = 'tr' | 'en';

// Map short codes to BCP 47
const SHORT_TO_BCP47: Record<string, LocaleCode> = {
  tr: 'tr-TR',
  en: 'en-US',
};

// Map BCP 47 to translation file key
const LOCALE_TO_TRANSLATION: Record<string, string> = {
  'tr-TR': 'tr',
  'en-US': 'en',
  'en-GB': 'en',
  'fr-FR': 'fr',
  'de-DE': 'de',
  'ru-RU': 'ru',
  'es-ES': 'es',
  'it-IT': 'it',
  'pt-PT': 'pt',
  'pt-BR': 'pt',
  'ar-SA': 'ar',
  'ar-AE': 'ar',
  'zh-CN': 'zh',
  'zh-TW': 'zh',
};

type TranslationObject = Record<string, unknown>;

const translations: Record<string, TranslationObject> = {
  tr: trTranslations as TranslationObject,
  en: enTranslations as TranslationObject,
  fr: (Array.isArray(frTranslations) ? frTranslations[0] : frTranslations) as TranslationObject,
  de: (Array.isArray(deTranslations) ? deTranslations[0] : deTranslations) as TranslationObject,
  ru: ruTranslations as TranslationObject,
  es: (Array.isArray(esTranslations) ? Object.assign({}, ...esTranslations) : esTranslations) as TranslationObject,
  it: (Array.isArray(itTranslations) ? itTranslations[0] : itTranslations) as TranslationObject,
  pt: (Array.isArray(ptTranslations) ? ptTranslations[0] : ptTranslations) as TranslationObject,
  ar: arTranslations as TranslationObject,
  zh: zhTranslations as TranslationObject,
};

// ─── Formatting Helpers ───────────────────────────────────────────────────────

export function formatDate(date: Date, locale: string, format: 'short' | 'long' | 'medium' = 'short'): string {
  try {
    if (format === 'long') {
      return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
    }
    if (format === 'medium') {
      return date.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
    }
    return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return date.toLocaleDateString('en-US');
  }
}

export function formatTime(date: Date, locale: string, hour12?: boolean): string {
  try {
    const use12 = hour12 ?? ['en-US', 'en-CA', 'hi-IN', 'ko-KR', 'ja-JP', 'zh-CN', 'zh-TW'].includes(locale);
    return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: use12 });
  } catch {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }
}

export function formatNumber(value: number, locale: string, decimals?: number): string {
  try {
    return value.toLocaleString(locale, decimals !== undefined ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals } : {});
  } catch {
    return value.toString();
  }
}

export function formatCurrency(value: number, locale: string, currency: string): string {
  try {
    return value.toLocaleString(locale, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
}

export function isRTL(localeCode: string): boolean {
  const config = LOCALE_CONFIGS.find(l => l.code === localeCode);
  return config?.rtl ?? false;
}

// ─── Context ──────────────────────────────────────────────────────────────────

interface LanguageContextValue {
  language: AppLanguage;
  locale: LocaleCode;
  setLanguage: (lang: AppLanguage) => void;
  setLocale: (locale: LocaleCode) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  toggleLanguage: () => void;
  isRTL: boolean;
  formatDate: (date: Date, format?: 'short' | 'long' | 'medium') => string;
  formatTime: (date: Date, hour12?: boolean) => string;
  formatNumber: (value: number, decimals?: number) => string;
  formatCurrency: (value: number, currency?: string) => string;
  currentLocaleConfig: typeof LOCALE_CONFIGS[number] | undefined;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: 'tr',
  locale: 'tr-TR',
  setLanguage: () => {},
  setLocale: () => {},
  t: (key: string) => key,
  toggleLanguage: () => {},
  isRTL: false,
  formatDate: (date) => date.toLocaleDateString(),
  formatTime: (date) => date.toLocaleTimeString(),
  formatNumber: (value) => value.toString(),
  formatCurrency: (value) => value.toFixed(2),
  currentLocaleConfig: undefined,
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleCode>('tr-TR');

  useEffect(() => {
    const stored = localStorage.getItem('nexpos_locale') as LocaleCode | null;
    // Legacy support
    const storedLang = localStorage.getItem('nexpos_language') as AppLanguage | null;
    if (stored && LOCALE_CONFIGS.find(l => l.code === stored)) {
      setLocaleState(stored);
    } else if (storedLang === 'en') {
      setLocaleState('en-US');
    } else if (storedLang === 'tr') {
      setLocaleState('tr-TR');
    }
  }, []);

  const setLocale = useCallback((lc: LocaleCode) => {
    setLocaleState(lc);
    localStorage.setItem('nexpos_locale', lc);
    // Legacy compat
    const lang = lc.startsWith('tr') ? 'tr' : 'en';
    localStorage.setItem('nexpos_language', lang);
  }, []);

  // Derive legacy AppLanguage from locale
  const language: AppLanguage = locale.startsWith('tr') ? 'tr' : 'en';

  const setLanguage = useCallback((lang: AppLanguage) => {
    const bcp47 = SHORT_TO_BCP47[lang] ?? 'en-US';
    setLocale(bcp47);
  }, [setLocale]);

  const toggleLanguage = useCallback(() => {
    setLanguage(language === 'tr' ? 'en' : 'tr');
  }, [language, setLanguage]);

  const translationKey = LOCALE_TO_TRANSLATION[locale] ?? 'en';

  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    const parts = key.split('.');
    let current: unknown = translations[translationKey];
    for (const part of parts) {
      if (current && typeof current === 'object' && part in (current as Record<string, unknown>)) {
        current = (current as Record<string, unknown>)[part];
      } else {
        // Fallback to English
        let fallback: unknown = translations['en'];
        for (const p of parts) {
          if (fallback && typeof fallback === 'object' && p in (fallback as Record<string, unknown>)) {
            fallback = (fallback as Record<string, unknown>)[p];
          } else {
            return key;
          }
        }
        current = fallback;
        break;
      }
    }
    if (typeof current !== 'string') return key;
    if (!params) return current;
    // Replace {param} placeholders
    return current.replace(/\{(\w+)\}/g, (_, k) => String(params[k] ?? `{${k}}`));
  }, [translationKey]);

  const rtl = useMemo(() => isRTL(locale), [locale]);
  const currentLocaleConfig = useMemo(() => LOCALE_CONFIGS.find(l => l.code === locale), [locale]);

  const fmtDate = useCallback((date: Date, format: 'short' | 'long' | 'medium' = 'short') => formatDate(date, locale, format), [locale]);
  const fmtTime = useCallback((date: Date, hour12?: boolean) => formatTime(date, locale, hour12), [locale]);
  const fmtNumber = useCallback((value: number, decimals?: number) => formatNumber(value, locale, decimals), [locale]);
  const fmtCurrency = useCallback((value: number, currency = 'TRY') => formatCurrency(value, locale, currency), [locale]);

  return (
    <LanguageContext.Provider value={{
      language,
      locale,
      setLanguage,
      setLocale,
      t,
      toggleLanguage,
      isRTL: rtl,
      formatDate: fmtDate,
      formatTime: fmtTime,
      formatNumber: fmtNumber,
      formatCurrency: fmtCurrency,
      currentLocaleConfig,
    }}>
      <div dir={rtl ? 'rtl' : 'ltr'} lang={locale}>
        {children}
      </div>
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  return useContext(LanguageContext);
}
